
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Alert,
  Card,
} from "react-bootstrap";

import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const { login } = useAuth();
  const { cart, clearCart } = useCart();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const result = await login(email, password);

      if (!result.success) {
        setError(result.message || "Invalid credentials.");
        return;
      }

      const user = result.user;

      if (user?.isAdmin) {
        clearCart();
        navigate("/admin/main", { replace: true });
        return;
      }

      if (cart?.length > 0) {
        navigate("/order-summary", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please try again.");
    }
  };

  return (
    <Container
      fluid
      className="login-page"
    >
      <style>{`
        /* =========================================================
           LOGIN PAGE
        ========================================================= */

        .login-page {
          min-height: 100vh;
          min-height: 100dvh;

          width: 100%;
          max-width: 100%;

          margin: 0;
          padding: 40px 20px;

          display: flex;
          align-items: center;
          justify-content: center;

          box-sizing: border-box;

          background:
            radial-gradient(
              circle at top left,
              rgba(59, 130, 246, 0.25),
              transparent 40%
            ),
            radial-gradient(
              circle at bottom right,
              rgba(124, 58, 237, 0.25),
              transparent 40%
            ),
            linear-gradient(
              135deg,
              #0f172a,
              #020617
            );

          overflow-x: hidden;
        }

        /* =========================================================
           LOGIN CARD
        ========================================================= */

        .login-card {
          width: 100%;
          max-width: 440px;

          margin: 0 auto;

          border-radius: 28px !important;

          border: 1px solid rgba(255, 255, 255, 0.08) !important;

          background: rgba(255, 255, 255, 0.08);

          backdrop-filter: blur(25px);
          -webkit-backdrop-filter: blur(25px);

          box-shadow:
            0 40px 100px rgba(0, 0, 0, 0.45);

          padding: 30px;

          box-sizing: border-box;
        }

        .login-card .card-body {
          padding: 0;
        }

        /* =========================================================
           TITLE
        ========================================================= */

        .login-title {
          font-size: 2rem;
          font-weight: 900;
          letter-spacing: -1px;
          color: #fff;
          line-height: 1.2;
        }

        .login-sub {
          color: rgba(255, 255, 255, 0.65);
          margin-top: 8px;
          margin-bottom: 20px;
          font-size: 0.95rem;
        }

        /* =========================================================
           INPUTS
        ========================================================= */

        .login-page .form-control {
          width: 100%;

          background: rgba(255, 255, 255, 0.06) !important;

          border: 1px solid rgba(255, 255, 255, 0.1) !important;

          color: white !important;

          border-radius: 14px !important;

          padding: 12px !important;

          min-height: 48px;

          box-shadow: none !important;

          box-sizing: border-box;
        }

        .login-page .form-control:focus {
          background: rgba(255, 255, 255, 0.09) !important;

          border-color: rgba(96, 165, 250, 0.7) !important;

          box-shadow:
            0 0 0 3px rgba(59, 130, 246, 0.15) !important;
        }

        .login-page .form-control::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        /* =========================================================
           BUTTON
        ========================================================= */

        .login-btn {
          width: 100%;

          min-height: 48px;

          border: none !important;

          border-radius: 14px;

          padding: 12px;

          font-weight: 800;

          background:
            linear-gradient(
              135deg,
              #2563eb,
              #4f46e5
            );

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .login-btn:hover {
          transform: translateY(-2px);

          box-shadow:
            0 20px 40px rgba(37, 99, 235, 0.3);
        }

        .login-btn:active {
          transform: translateY(0);
        }

        /* =========================================================
           LINKS
        ========================================================= */

        .login-link {
          color: #60a5fa;

          text-decoration: none;

          font-weight: 600;
        }

        .login-link:hover {
          color: #93c5fd;
          text-decoration: underline;
        }

        /* =========================================================
           FOOTER
        ========================================================= */

        .login-foot {
          margin-top: 20px;

          font-size: 12px;

          line-height: 1.5;

          color: rgba(255, 255, 255, 0.5);

          text-align: center;
        }

        /* =========================================================
           ALERT
        ========================================================= */

        .login-page .alert {
          border-radius: 14px !important;

          font-size: 0.9rem;

          word-break: break-word;
        }

        /* =========================================================
           TABLET
        ========================================================= */

        @media (max-width: 768px) {
          .login-page {
            padding: 30px 16px;
          }

          .login-card {
            width: 100%;
            max-width: 440px;

            margin: 0 auto;

            padding: 26px;
          }

          .login-title {
            font-size: 1.7rem;
          }
        }

        /* =========================================================
           MOBILE
        ========================================================= */

        @media (max-width: 576px) {
          .login-page {
            padding: 24px 14px;

            align-items: center;
            justify-content: center;
          }

          .login-card {
            width: 100%;
            max-width: 100%;

            margin: 0 auto;

            padding: 22px 18px;

            border-radius: 22px !important;
          }

          .login-title {
            font-size: 1.55rem;
          }

          .login-sub {
            font-size: 0.88rem;
          }

          .login-foot {
            font-size: 11px;
          }
        }

        /* =========================================================
           VERY SMALL PHONES
        ========================================================= */

        @media (max-width: 380px) {
          .login-page {
            padding: 18px 10px;
          }

          .login-card {
            padding: 20px 15px;

            border-radius: 20px !important;
          }

          .login-title {
            font-size: 1.4rem;
          }
        }

        /* =========================================================
           LANDSCAPE MOBILE
        ========================================================= */

        @media (max-height: 600px) and (orientation: landscape) {
          .login-page {
            align-items: flex-start;

            padding-top: 20px;
            padding-bottom: 20px;
          }

          .login-card {
            margin-top: 10px;
            margin-bottom: 10px;
          }
        }
      `}</style>

      {/* =========================================================
          LOGIN CARD
      ========================================================= */}

      <Card className="login-card">
        <Card.Body>
          <div className="text-center mb-4">
            <div className="login-title">
              Welcome Back
            </div>

            <div className="login-sub">
              Sign in to continue to MyIdeaShop
            </div>
          </div>

          {error && (
            <Alert
              variant="danger"
              className="rounded-3"
            >
              {error}
            </Alert>
          )}

          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                value={email}
                placeholder="Email address"
                autoComplete="email"
                onChange={(e) =>
                  setEmail(e.target.value)
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Control
                type="password"
                value={password}
                placeholder="Password"
                autoComplete="current-password"
                onChange={(e) =>
                  setPassword(e.target.value)
                }
                required
              />
            </Form.Group>

            <div className="text-end mb-3">
              <Link
                to="/forgot-password"
                className="login-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="login-btn"
            >
              Sign In
            </Button>
          </Form>

          <div className="text-center mt-4">
            <span
              style={{
                color: "rgba(255,255,255,0.6)",
              }}
            >
              New here?
            </span>{" "}

            <Link
              to="/register"
              className="login-link"
            >
              Create account
            </Link>
          </div>

          <div className="login-foot">
            Secure login • Encrypted session •
            Trusted checkout system
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Login;

